import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext.jsx';
import { apiClient } from '../../../lib/apiClient.js';

function ChevronDown() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function ChevronUp() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
      <path d="M18 15l-6-6-6 6" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );
}

function BeneficiaryIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function AddressIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

// ── Beneficiary section ────────────────────────────────────────────────────────

const BENEFICIARY_FIELDS = [
  { label: 'Beneficiary full name', required: true },
  { label: 'Relationship', hint: 'spouse, child, parent, sibling, estate, or other', required: true },
  { label: 'Allocation %', hint: 'if multiple beneficiaries, must total 100%', required: false },
  { label: 'Contingent beneficiary', hint: 'backup if primary beneficiary predeceases you', required: false },
];

const BENEFICIARY_EXAMPLES = [
  'Change my beneficiary to my spouse Jane Doe',
  'Add my two children as 50/50 primary beneficiaries',
  'Update my primary beneficiary and add a contingent beneficiary',
];

function CurrentBeneficiaries({ beneficiaries }) {
  const primary    = beneficiaries?.primary    || [];
  const contingent = beneficiaries?.contingent || [];

  if (primary.length === 0 && contingent.length === 0) {
    return (
      <div className="text-xs text-text-faint bg-bg-s2 border border-border rounded-lg px-3 py-2 mb-4">
        No beneficiary on file — consider designating one.
      </div>
    );
  }

  return (
    <div className="mb-4 space-y-2">
      <p className="text-[11px] font-mono uppercase tracking-widest text-text-faint">Current designation</p>
      {primary.map((b, i) => (
        <div key={i} className="flex items-center justify-between text-[13px] bg-bg-s2 rounded-lg px-3 py-2 border border-border">
          <span className="font-medium text-text">{b.name}</span>
          <span className="text-text-muted capitalize">{b.relationship} · {b.allocation_pct}% primary</span>
        </div>
      ))}
      {contingent.map((b, i) => (
        <div key={i} className="flex items-center justify-between text-[13px] bg-bg-s2 rounded-lg px-3 py-2 border border-border">
          <span className="font-medium text-text">{b.name}</span>
          <span className="text-text-muted capitalize">{b.relationship} · {b.allocation_pct}% contingent</span>
        </div>
      ))}
    </div>
  );
}

function formatTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  );
}

function InlineHistoryPanel({ items, title }) {
  if (items.length === 0) return null;
  return (
    <div className="border-t border-border pt-4 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-mono uppercase tracking-widest text-text-faint">{title}</p>
        <span className="text-[10px] font-mono text-text-faint bg-bg-s3 px-2 py-0.5 rounded-full border border-border">
          {items.length} {items.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>
      <div className="space-y-1.5">
        {items.map((item, idx) => (
          <div key={item.id || idx} className="rounded-lg border border-border bg-bg-s3 px-3.5 py-2.5 space-y-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-[11px] text-text-faint font-mono">{formatTs(item.timestamp)}</p>
              {item.change_source && (
                <p className="text-[10px] text-text-faint">{item.change_source}</p>
              )}
            </div>
            {item.field_name === 'address' && (() => {
              const nv = item.new_value || {};
              const ov = item.old_value || {};
              return (
                <div className="text-xs space-y-0.5">
                  {ov.street_address && (
                    <p className="text-text-faint line-through">
                      {[ov.street_address, ov.city, ov.state, ov.zip_code].filter(Boolean).join(', ')}
                    </p>
                  )}
                  <p className="text-text font-medium">
                    {[nv.street_address, nv.city, nv.state, nv.zip_code].filter(Boolean).join(', ')}
                  </p>
                </div>
              );
            })()}
            {item.field_name === 'beneficiary' && (() => {
              const primaries = item.new_value?.primary || [];
              return (
                <div className="text-xs space-y-0.5">
                  {primaries.length === 0 ? (
                    <p className="text-text-faint italic">No primary beneficiaries on file</p>
                  ) : primaries.map((b, i) => (
                    <p key={i} className="text-text">
                      <span className="font-medium">{b.name}</span>
                      <span className="text-text-faint ml-1">({b.relationship} · {b.allocation_pct}%)</span>
                    </p>
                  ))}
                </div>
              );
            })()}
          </div>
        ))}
      </div>
    </div>
  );
}

function BeneficiarySection({ isOpen, onToggle, beneficiaries, history = [], blackoutActive = false }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  function launchChat(text) {
    navigate('/participant/chat', { state: { chatDraft: text } });
  }

  return (
    <div className={['card overflow-hidden transition-shadow duration-200', isOpen ? 'shadow-card-hover border-accent/30' : ''].join(' ')}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-5 text-left hover:bg-bg-s2 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
      >
        <div className={['w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200', isOpen ? 'bg-accent text-white shadow-md' : 'bg-accent-light text-accent-dark'].join(' ')}>
          <BeneficiaryIcon />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[15px] font-semibold text-text">Beneficiary Update</span>
            <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full border bg-purple-50 text-purple-700 border-purple-200">
              Queued for review
            </span>
          </div>
          <p className="text-xs text-text-faint mt-0.5">ERISA §205 · ERISA §206(d)</p>
        </div>
        <span className="text-text-faint flex-shrink-0">{isOpen ? <ChevronUp /> : <ChevronDown />}</span>
      </button>

      {isOpen && (
        <div className="border-t border-border px-5 pb-5 space-y-5 pt-4">
          <div>
            <p className="text-sm text-text-muted leading-relaxed">
              Name or change who receives your account balance if you pass away. Naming anyone other than your spouse
              as primary beneficiary may require notarized spousal consent under ERISA §205. The plan administrator
              must review and approve before the change takes effect.
            </p>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-text-faint bg-bg-s2 border border-border px-3 py-1 rounded-full">
              <span>📋</span>
              <span>Queued for plan administrator review</span>
            </div>
          </div>

          <CurrentBeneficiaries beneficiaries={beneficiaries} />

          <div>
            <h4 className="text-xs font-mono uppercase tracking-widest text-text-faint mb-3">What to mention</h4>
            <ul className="space-y-2">
              {BENEFICIARY_FIELDS.map((f) => (
                <li key={f.label} className="flex items-start gap-3">
                  <div className={['w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5', f.required ? 'bg-accent-light' : 'bg-bg-s3'].join(' ')}>
                    <span className={['text-[9px] font-bold', f.required ? 'text-accent-dark' : 'text-text-faint'].join(' ')}>{f.required ? '!' : '?'}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-text">{f.label}</span>
                    {f.hint && <span className="text-xs text-text-muted ml-1.5">— {f.hint}</span>}
                    {!f.required && <span className="ml-1.5 text-[10px] font-mono text-text-faint bg-bg-s3 px-1.5 py-0.5 rounded">optional</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-mono uppercase tracking-widest text-text-faint mb-3">Example queries — click to use</h4>
            <div className="space-y-2">
              {BENEFICIARY_EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => setQuery(ex)}
                  className={['w-full text-left text-sm px-4 py-3 rounded-lg border transition-colors duration-150', query === ex ? 'border-accent bg-accent-light text-accent-dark font-medium' : 'border-border text-text-muted hover:border-accent/40 hover:bg-bg-s2 hover:text-text'].join(' ')}
                >
                  <span className="font-mono text-accent opacity-60 mr-2">"</span>{ex}<span className="font-mono text-accent opacity-60 ml-0.5">"</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-mono uppercase tracking-widest text-text-faint mb-2">Or write your own</h4>
            <textarea
              rows={2}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`e.g. "${BENEFICIARY_EXAMPLES[0]}"`}
              className="w-full border border-border-strong rounded-xl px-4 py-3 text-sm text-text bg-bg outline-none resize-none focus:border-accent focus:ring-2 focus:ring-accent/10 placeholder:text-text-faint leading-relaxed transition-all duration-150"
            />
            <button
              type="button"
              onClick={() => { if (query.trim() && !blackoutActive) launchChat(query.trim()); }}
              disabled={!query.trim() || blackoutActive}
              title={blackoutActive ? 'Plan blackout in effect — requests disabled' : undefined}
              className="mt-2 w-full btn-primary flex items-center justify-center gap-2"
            >
              <SendIcon />
              {blackoutActive ? 'Blackout in effect' : 'Send to chat'}
            </button>
          </div>

          <InlineHistoryPanel items={history} title="Beneficiary Change History" />
        </div>
      )}
    </div>
  );
}

// ── Address section ────────────────────────────────────────────────────────────

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

function CurrentAddress({ address }) {
  const hasAddress = address?.street_address || address?.city;
  if (!hasAddress) return null;

  return (
    <div className="mb-4 p-3 bg-bg-s2 border border-border rounded-lg text-sm">
      <p className="text-[11px] font-mono uppercase tracking-widest text-text-faint mb-1">Current address on file</p>
      <p className="text-text font-medium">{address.street_address}</p>
      <p className="text-text-muted">{address.city}{address.state ? `, ${address.state}` : ''} {address.zip_code}</p>
    </div>
  );
}

function AddressSection({ isOpen, onToggle, currentAddress, onAddressUpdated, history = [], blackoutActive = false }) {
  const [form, setForm] = useState({ streetAddress: '', city: '', state: '', zipCode: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  function setField(key, val) {
    setForm(prev => ({ ...prev, [key]: val }));
    setSuccess(null);
    setError(null);
  }

  const isValid = form.streetAddress.trim() && form.city.trim() && form.state && form.zipCode.trim();

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await apiClient.updateAddress({
        streetAddress: form.streetAddress.trim(),
        city:          form.city.trim(),
        state:         form.state,
        zipCode:       form.zipCode.trim(),
      });
      setSuccess(`Address updated to ${form.streetAddress.trim()}, ${form.city.trim()}, ${form.state} ${form.zipCode.trim()}.`);
      setForm({ streetAddress: '', city: '', state: '', zipCode: '' });
      if (onAddressUpdated) onAddressUpdated();
    } catch (err) {
      setError(err.message || 'Failed to update address. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={['card overflow-hidden transition-shadow duration-200', isOpen ? 'shadow-card-hover border-accent/30' : ''].join(' ')}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-5 text-left hover:bg-bg-s2 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
      >
        <div className={['w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200', isOpen ? 'bg-accent text-white shadow-md' : 'bg-accent-light text-accent-dark'].join(' ')}>
          <AddressIcon />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[15px] font-semibold text-text">Mailing Address Update</span>
            <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full border bg-green-50 text-green-700 border-green-200">
              Executes immediately
            </span>
          </div>
          <p className="text-xs text-text-faint mt-0.5">ERISA §101 · IRC §402(f)</p>
        </div>
        <span className="text-text-faint flex-shrink-0">{isOpen ? <ChevronUp /> : <ChevronDown />}</span>
      </button>

      {isOpen && (
        <div className="border-t border-border px-5 pb-5 pt-4 space-y-5">
          <div>
            <p className="text-sm text-text-muted leading-relaxed">
              Update the mailing address on file for your retirement account. Your address is used for plan notices,
              tax forms (1099-R), and required ERISA disclosures. Changes take effect immediately — no administrator
              review required.
            </p>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-text-faint bg-bg-s2 border border-border px-3 py-1 rounded-full">
              <span>⚡</span>
              <span>Executes immediately — no review required</span>
            </div>
          </div>

          <CurrentAddress address={currentAddress} />

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block mb-1.5 text-xs font-mono uppercase tracking-widest text-text-faint">
                Street Address <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                value={form.streetAddress}
                onChange={(e) => setField('streetAddress', e.target.value)}
                placeholder="e.g. 123 Main St, Apt 4B"
                className="input-field"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block mb-1.5 text-xs font-mono uppercase tracking-widest text-text-faint">
                  City <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => setField('city', e.target.value)}
                  placeholder="Chicago"
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block mb-1.5 text-xs font-mono uppercase tracking-widest text-text-faint">
                  State <span className="text-danger">*</span>
                </label>
                <select
                  value={form.state}
                  onChange={(e) => setField('state', e.target.value)}
                  className="input-field"
                  required
                >
                  <option value="">Select state</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="max-w-[180px]">
              <label className="block mb-1.5 text-xs font-mono uppercase tracking-widest text-text-faint">
                Zip Code <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                value={form.zipCode}
                onChange={(e) => setField('zipCode', e.target.value)}
                placeholder="60601"
                maxLength={10}
                className="input-field"
                required
              />
            </div>

            {error && (
              <p className="text-sm text-danger bg-danger/8 border border-danger/20 rounded-lg px-4 py-2.5">
                {error}
              </p>
            )}
            {success && (
              <p className="text-sm text-success bg-success/8 border border-success/20 rounded-lg px-4 py-2.5">
                ✓ {success}
              </p>
            )}

            <button
              type="submit"
              disabled={!isValid || submitting || blackoutActive}
              title={blackoutActive ? 'Plan blackout in effect — requests disabled' : undefined}
              className="w-full btn-primary flex items-center justify-center gap-2"
            >
              {submitting ? 'Updating…' : blackoutActive ? 'Blackout in effect' : 'Update Address'}
            </button>
          </form>

          <InlineHistoryPanel items={history} title="Address Change History" />
        </div>
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────────

export default function BeneficiaryAndAddressPage() {
  const { principal } = useAuth();
  const [expanded, setExpanded] = useState(null);
  const [profile, setProfile] = useState(null);
  const [activity, setActivity] = useState([]);
  const [blackoutActive, setBlackoutActive] = useState(false);

  async function loadProfile() {
    try {
      const [data, actRes, participantsRes] = await Promise.all([
        apiClient.getParticipantProfile(),
        apiClient.getParticipantActivity().catch(() => ({ activities: [] })),
        apiClient.listParticipants().catch(() => ({ participants: [] })),
      ]);
      setProfile(data);
      setActivity(actRes.activities || []);
      const me = (participantsRes?.participants ?? []).find(
        (p) => p.participant_id === principal?.participantId
      );
      setBlackoutActive(me?.blackout_active ?? false);
    } catch {
      // non-fatal — sections still work without current data
    }
  }

  useEffect(() => { loadProfile(); }, []);

  function toggle(key) {
    setExpanded(prev => (prev === key ? null : key));
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Hero card */}
      <div className="card overflow-hidden flex">
        <div className="w-1 bg-accent flex-shrink-0" />
        <div className="p-5 flex-1">
          <h1 className="text-[20px] font-bold text-text leading-tight">Beneficiary &amp; Address</h1>
          <p className="text-sm text-text-muted mt-1 leading-relaxed">
            Update who receives your account upon your death, or change your mailing address on file.
            Beneficiary changes are submitted via chat and routed to the plan administrator for approval
            (ERISA §205). Address changes are a direct update and take effect immediately.
          </p>
        </div>
      </div>

      {blackoutActive && (
        <div className="card p-4 border-warning/40 bg-warning/5 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-warning/15 text-warning flex items-center justify-center text-sm font-bold flex-shrink-0">!</div>
          <div>
            <p className="text-[13px] font-semibold text-warning">Plan blackout in effect</p>
            <p className="text-[12px] text-text-muted mt-0.5">
              All account writes are temporarily suspended per ERISA §101(i). Changes cannot be submitted until the blackout ends.
            </p>
          </div>
        </div>
      )}

      <BeneficiarySection
        isOpen={expanded === 'beneficiary'}
        onToggle={() => toggle('beneficiary')}
        beneficiaries={profile?.beneficiaries}
        history={activity.filter((a) => a.field_name === 'beneficiary')}
        blackoutActive={blackoutActive}
      />
      <AddressSection
        isOpen={expanded === 'address'}
        onToggle={() => toggle('address')}
        currentAddress={profile?.address}
        onAddressUpdated={loadProfile}
        history={activity.filter((a) => a.field_name === 'address')}
        blackoutActive={blackoutActive}
      />
    </div>
  );
}
